/**
 * Output extraction, one-line formatting, and field-level diffing for compared cases.
 *
 * Agent outputs come in a few shapes (a classification's `functionCalls[0].arguments`,
 * a mail-digest dossier object, a `{text}` blob, or an `{error}`). `extractPayload`
 * reduces any of them to the meaningful value; `diffFields` aligns two-or-more payloads
 * field-by-field for the drilldown.
 *
 * Note: array diffing here is index-based (good enough for these object-shaped payloads).
 * If list-heavy outputs appear, swap `flatten` for an LCS diff (e.g. jsondiffpatch with
 * objectHash) — the rest of the API stays the same.
 */

// Agent output is free-form JSON; a precise type adds friction without safety here.
type Json = any;

/** Reduce a raw agent output to the value worth showing/diffing. */
export const extractPayload = (output: Json): Json => {
  if (!output || typeof output !== "object") return output ?? null;
  if (output.error) return { error: output.error };
  if (Array.isArray(output.functionCalls) && output.functionCalls.length > 0) {
    return output.functionCalls[0]?.arguments ?? output.functionCalls[0];
  }
  if (typeof output.text === "string" && Object.keys(output).length === 1) {
    return output.text;
  }
  return output;
};

/** Compact one-line summary of an output, for table cells and tooltips. */
export const formatOutput = (output: Json): string => {
  const payload = extractPayload(output);
  if (payload == null) return "—";
  if (typeof payload !== "object") return String(payload) || "—";
  if (payload.error) return `Error: ${payload.error}`;
  const values = Object.values(payload);
  return values.length === 1 ? String(values[0]) : JSON.stringify(payload);
};

export type DiffStatus = "same" | "added" | "removed" | "changed";

export interface FieldDiff {
  path: string;
  /** Stringified value per run in column order (undefined = field absent for that run). */
  values: (string | undefined)[];
  /** Status of each run vs the baseline (index 0 is always "same"). */
  statuses: DiffStatus[];
  /** Convenience flag: any run differs from baseline. */
  changed: boolean;
}

const flatten = (
  value: Json,
  prefix = "",
  out: Record<string, string> = {},
): Record<string, string> => {
  if (value === null || value === undefined) {
    out[prefix || "(value)"] = String(value);
    return out;
  }
  if (typeof value !== "object") {
    out[prefix || "(value)"] = String(value);
    return out;
  }
  if (Array.isArray(value)) {
    if (value.length === 0) out[prefix || "(value)"] = "[]";
    else value.forEach((v, i) => flatten(v, `${prefix}[${i}]`, out));
    return out;
  }
  const keys = Object.keys(value);
  if (keys.length === 0) out[prefix || "(value)"] = "{}";
  else
    keys.forEach((k) => flatten(value[k], prefix ? `${prefix}.${k}` : k, out));
  return out;
};

/**
 * Diff N payloads field-by-field against the first (baseline). Returns one row per
 * field path (union across all payloads), each carrying the per-run value + status.
 */
export const diffFields = (payloads: Json[]): FieldDiff[] => {
  const flats = payloads.map((p) => flatten(extractPayload(p)));
  const baseline = flats[0] ?? {};
  const paths = Array.from(
    new Set(flats.flatMap((f) => Object.keys(f))),
  ).sort();

  return paths.map((path) => {
    const values = flats.map((f) => f[path]);
    let changed = false;
    const statuses: DiffStatus[] = flats.map((f, i) => {
      if (i === 0) return "same";
      const base = baseline[path];
      const cur = f[path];
      if (base === cur) return "same";
      changed = true;
      if (base === undefined) return "added";
      if (cur === undefined) return "removed";
      return "changed";
    });
    return { path, values, statuses, changed };
  });
};
